import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SaveButton } from '../../../components/mui/Buttons/Buttons';
import {
  awardSkillxManualXp,
  getSkillxXpRules,
  getSkillxLearnerXp,
  getSkillxBatchLeaderboard,
} from '../../../redux/features/skillxSlice';
import { SX, levelFor } from '../leaderboard/leaderboardData';

/**
 * Award XP by hand.
 *
 * The mentor's half of the XP system: the points that no producer can observe —
 * a presentation delivered, a classmate unblocked, a session someone carried.
 *
 * Three things this dialog does deliberately:
 *
 *   - It asks for the BATCH first, and picks the learner from that batch's
 *     roster. XP is stored per (learner, batch) and the leaderboard groups by
 *     batch, so an award into the wrong batch lands on a board nobody is
 *     looking at. Choosing the roster first makes that impossible rather than
 *     merely validated (the server checks it too).
 *   - It shows what the learner already has before the award is made. Stacking
 *     three "great participation" awards in a week is the failure mode of a
 *     manual XP feature, and the fix is showing the last few awards, not a
 *     rule.
 *   - Remarks are required. They are the audit; an award with no reason is an
 *     unexplained jump in someone's rank.
 *
 * Every ACTIVE rule is offered, including the ones an automatic producer also
 * writes (daily login, live-class attendance, punctuality, assignments) —
 * correcting attendance the producer missed is a normal mentor task. Those are
 * marked "Automatic" in the list and warned about once chosen, since awarding
 * one the producer already paid for credits the same action twice. The award is
 * revocable, which is the real safety net.
 *
 * Rules and roster are fetched into local state rather than through the slice:
 * the XP Settings page keeps `xpRules` in redux with retired rules included,
 * and this dialog wants only the active ones. Two different questions, one key
 * — so this one stays local.
 */

const MAX_MANUAL_POINTS = 500; // mirrors the server-side cap

// Rules an automatic producer also writes. The rules endpoint returns
// `is_producer_owned` per rule; this list is the fallback for a server that
// predates that field, and mirrors PRODUCER_OWNED_RULES in
// skillxXp.controller.js.
const PRODUCER_OWNED_RULES = [
  'daily_login',
  'live_class',
  'on_time',
  'assignment',
  'assignment_early',
];

const isProducerOwned = (rule) =>
  rule?.is_producer_owned ?? PRODUCER_OWNED_RULES.includes(rule?.rule_key);

const ValidationSchema = Yup.object().shape({
  batchId: Yup.number().typeError('Choose a batch').required('Batch is required'),
  admnNo: Yup.number().typeError('Choose a learner').required('Learner is required'),
  ruleKey: Yup.string().required('Choose what the XP is for'),
  points: Yup.number()
    .typeError('Points must be a number')
    .integer('Points must be a whole number')
    .min(1, 'Points must be at least 1')
    .max(MAX_MANUAL_POINTS, `A single award is capped at ${MAX_MANUAL_POINTS} XP`)
    .required('Points are required'),
  remarks: Yup.string()
    .trim()
    .required('Say what the XP is for — this is the audit record')
    .min(5, 'A few more words, please')
    .max(500, 'Remarks are too long'),
});

// The batch list is every batch the academy has ever run; cap what the dropdown
// renders and let typing reach the rest (same as the leaderboard filter).
const filterBatchOptions = createFilterOptions({
  limit: 100,
  stringify: (option) => option.course_batch_name || '',
});

export default function AwardXpDialog({ dispatchparams, onAwarded }) {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading } = useSelector((state) => state.skillx);
  const { batchesAll } = useSelector((state) => state.batch);

  // Every active rule — fetched once per dialog session.
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);

  // The chosen batch's roster.
  const [learners, setLearners] = useState([]);
  const [learnersLoading, setLearnersLoading] = useState(false);

  // The chosen learner's current XP — context, not input.
  const [learnerXp, setLearnerXp] = useState(null);
  const [learnerXpLoading, setLearnerXpLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      batchId: '',
      admnNo: '',
      ruleKey: '',
      points: '',
      remarks: '',
    },
    validationSchema: ValidationSchema,
    onSubmit: (values, { resetForm }) => {
      dispatch(
        awardSkillxManualXp({
          admnNo: Number(values.admnNo),
          batchId: Number(values.batchId),
          ruleKey: values.ruleKey,
          points: Number(values.points),
          remarks: values.remarks.trim(),
        }),
      ).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          if (onAwarded) onAwarded(dispatchparams);
          resetForm();
          setLearnerXp(null);
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || 'Failed to award XP', { variant: 'error' });
        }
      });
    },
  });

  const { errors, touched, values, handleChange, handleSubmit, setFieldValue } = formik;

  // Every active rule, once the dialog is opened. Not `manualOnly` — that flag
  // decides what an admin marked as mentor-facing, and a mentor still needs to
  // reach the automatic ones to correct what a producer missed.
  useEffect(() => {
    if (!open || rules.length) return;
    setRulesLoading(true);
    dispatch(getSkillxXpRules({}))
      .unwrap()
      .then((res) => setRules(res?.data || []))
      .catch(() => setRules([]))
      .finally(() => setRulesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The roster follows the batch. Reuses the leaderboard endpoint, which
  // already returns every active learner in a batch with their XP — so the
  // picker can show what each learner currently has without a second call.
  useEffect(() => {
    if (!values.batchId) {
      setLearners([]);
      return;
    }
    let cancelled = false;
    setLearnersLoading(true);
    dispatch(getSkillxBatchLeaderboard(values.batchId))
      .unwrap()
      .then((res) => {
        if (!cancelled) setLearners(res?.data?.students || []);
      })
      .catch(() => {
        if (!cancelled) setLearners([]);
      })
      .finally(() => {
        if (!cancelled) setLearnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, values.batchId]);

  // The chosen learner's history — the "have they already been given this?" check.
  useEffect(() => {
    if (!values.admnNo || !values.batchId) {
      setLearnerXp(null);
      return;
    }
    let cancelled = false;
    setLearnerXpLoading(true);
    dispatch(getSkillxLearnerXp({ admnNo: values.admnNo, batchId: values.batchId }))
      .unwrap()
      .then((res) => {
        if (!cancelled) setLearnerXp(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setLearnerXp(null);
      })
      .finally(() => {
        if (!cancelled) setLearnerXpLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, values.admnNo, values.batchId]);

  const selectedBatch = useMemo(
    () => (batchesAll || []).find((b) => b.course_batch_id === values.batchId) || null,
    [batchesAll, values.batchId],
  );
  const selectedLearner = useMemo(
    () => learners.find((l) => l.id === values.admnNo) || null,
    [learners, values.admnNo],
  );
  const selectedRule = useMemo(
    () => rules.find((r) => r.rule_key === values.ruleKey) || null,
    [rules, values.ruleKey],
  );

  const pointsOverridden =
    selectedRule && values.points !== '' && Number(values.points) !== selectedRule.points;

  return (
    <GlobalDialog
      title="Award XP"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="md"
      closeButton
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Award XP
        </AddButton>
      }
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
              {/* --- who --- */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Autocomplete
                  fullWidth
                  options={batchesAll || []}
                  filterOptions={filterBatchOptions}
                  getOptionLabel={(option) => option.course_batch_name || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.course_batch_id === value?.course_batch_id
                  }
                  value={selectedBatch}
                  onChange={(e, option) => {
                    setFieldValue('batchId', option?.course_batch_id ?? '');
                    // A new batch means a new roster; the old learner is not on it.
                    setFieldValue('admnNo', '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Batch"
                      autoComplete="off"
                      error={touched.batchId && Boolean(errors.batchId)}
                      helperText={
                        (touched.batchId && errors.batchId) ||
                        'XP is earned per batch — this is the leaderboard it lands on'
                      }
                    />
                  )}
                />

                <Autocomplete
                  fullWidth
                  disabled={!values.batchId}
                  loading={learnersLoading}
                  options={learners}
                  getOptionLabel={(option) => (option?.name ? `${option.name} (${option.id})` : '')}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  value={selectedLearner}
                  onChange={(e, option) => setFieldValue('admnNo', option?.id ?? '')}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Stack sx={{ width: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: SX.blue }}>
                            {(option.xp || 0).toLocaleString()} XP
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>
                          Adm. no {option.id}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Learner"
                      autoComplete="off"
                      error={touched.admnNo && Boolean(errors.admnNo)}
                      helperText={
                        (touched.admnNo && errors.admnNo) ||
                        (values.batchId ? `${learners.length} learners on this roster` : 'Choose a batch first')
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {learnersLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>

              {/* --- what the learner already has --- */}
              {values.admnNo ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: `${SX.radius.sm}px`,
                    background: SX.cardSoft,
                    border: `1px solid ${SX.border}`,
                  }}
                >
                  {learnerXpLoading && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={15} />
                      <Typography sx={{ fontSize: 12.5, color: SX.textMuted }}>
                        Loading this learner&apos;s XP…
                      </Typography>
                    </Stack>
                  )}

                  {!learnerXpLoading && learnerXp && (
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Icon icon="solar:bolt-bold-duotone" width={16} height={16} color={SX.blue} />
                          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: SX.text }}>
                            {(learnerXp.totalXp || 0).toLocaleString()} XP
                          </Typography>
                        </Stack>
                        <Chip
                          size="small"
                          icon={<Icon icon={levelFor(learnerXp.totalXp || 0).icon} width={13} height={13} />}
                          label={levelFor(learnerXp.totalXp || 0).name}
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            background: `${levelFor(learnerXp.totalXp || 0).color}14`,
                            color: levelFor(learnerXp.totalXp || 0).color,
                          }}
                        />
                        {values.points !== '' && Number(values.points) > 0 && (
                          <Typography sx={{ fontSize: 12, color: SX.textMuted }}>
                            → {((learnerXp.totalXp || 0) + Number(values.points)).toLocaleString()} XP
                            after this award
                          </Typography>
                        )}
                      </Stack>

                      {learnerXp.manualAwards?.length > 0 && (
                        <>
                          <Divider />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: SX.textMuted }}>
                            RECENT HAND-AWARDS
                          </Typography>
                          <Stack spacing={0.5}>
                            {learnerXp.manualAwards.slice(0, 3).map((a) => (
                              <Stack
                                key={a.id}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ opacity: a.status === 'REVOKED' ? 0.5 : 1 }}
                              >
                                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: SX.blue, minWidth: 42 }}>
                                  {a.status === 'REVOKED' ? '—' : `+${a.points}`}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: 11.5, color: SX.textMuted, flex: 1 }}>
                                  {a.remarks}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: SX.textMuted }}>
                                  {a.awarded_by_name || ''}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </>
                      )}
                    </Stack>
                  )}
                </Box>
              ) : null}

              {/* --- what for --- */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Autocomplete
                  fullWidth
                  loading={rulesLoading}
                  options={rules}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(option, value) => option.rule_key === value?.rule_key}
                  value={selectedRule}
                  onChange={(e, option) => {
                    setFieldValue('ruleKey', option?.rule_key ?? '');
                    // Prefill the configured value — an override is possible
                    // but the admin's number should be the starting point.
                    setFieldValue('points', option?.points ?? '');
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.rule_key}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                        <Icon
                          icon={option.icon || 'solar:star-shine-bold-duotone'}
                          width={17}
                          height={17}
                          color={SX.purple}
                        />
                        <Typography sx={{ fontSize: 13.5, flex: 1 }}>{option.label}</Typography>
                        {isProducerOwned(option) && (
                          <Chip
                            size="small"
                            icon={<Icon icon="solar:cpu-bolt-bold-duotone" width={12} height={12} />}
                            label="Automatic"
                            sx={{
                              height: 20,
                              fontSize: 10.5,
                              fontWeight: 700,
                              background: '#f4f6f8',
                              color: SX.textMuted,
                            }}
                          />
                        )}
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: SX.blue }}>
                          {option.points} XP
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="What is this XP for?"
                      autoComplete="off"
                      error={touched.ruleKey && Boolean(errors.ruleKey)}
                      helperText={
                        (touched.ruleKey && errors.ruleKey) ||
                        'Every active activity, including the ones the portal awards automatically'
                      }
                    />
                  )}
                />

                <TextField
                  name="points"
                  label="Points"
                  type="number"
                  sx={{ width: { xs: '100%', sm: 180 } }}
                  value={values.points}
                  onChange={handleChange}
                  error={touched.points && Boolean(errors.points)}
                  helperText={
                    (touched.points && errors.points) ||
                    (selectedRule ? `Rule default: ${selectedRule.points}` : `Max ${MAX_MANUAL_POINTS}`)
                  }
                />
              </Stack>

              {selectedRule && isProducerOwned(selectedRule) && (
                <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                  “{selectedRule.label}” is normally awarded by the portal itself. Award it here
                  only when the automatic one did not land — if the learner already has it for
                  this, they will be credited twice. Say which occasion it is for in the remarks.
                </Alert>
              )}

              {pointsOverridden && (
                <Alert severity="info" sx={{ fontSize: 12.5 }}>
                  You are awarding <b>{values.points}</b> instead of the configured{' '}
                  <b>{selectedRule.points}</b> for “{selectedRule.label}”. The override is
                  recorded against your name in the audit.
                </Alert>
              )}

              {/* --- why --- */}
              <TextField
                name="remarks"
                label="Remarks"
                fullWidth
                multiline
                minRows={3}
                value={values.remarks}
                onChange={handleChange}
                error={touched.remarks && Boolean(errors.remarks)}
                helperText={
                  (touched.remarks && errors.remarks) ||
                  'Required. What did the learner do? This is what the audit shows, and what makes the award defensible later.'
                }
              />

              <Alert severity="warning" sx={{ fontSize: 12.5 }}>
                This award is attributed to you and appears in the XP audit history immediately.
                It can be revoked from there, which takes the points back off the learner&apos;s
                total.
              </Alert>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>
                <SaveButton disabled={loading} size="small" type="submit">
                  {loading ? 'Awarding...' : 'Award XP'}
                </SaveButton>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
}
